<template>
  <div>

    <!-- ═══════════════════════ MOBILE ═══════════════════════ -->
    <div v-if="isMobileOrTablet" class="pb-5">
      <appHeader></appHeader>
      <div class="container-fluid pt-4 mt-2">

        <!-- Stepper -->
        <div class="cart-stepper mb-4">
          <div class="cs-step done">
            <div class="cs-dot"><font-awesome-icon icon="check" /></div>
            <span>Items</span>
          </div>
          <div class="cs-line"></div>
          <div class="cs-step active">
            <div class="cs-dot">2</div>
            <span>Shipping</span>
          </div>
          <div class="cs-line"></div>
          <div class="cs-step">
            <div class="cs-dot">3</div>
            <span>Summary</span>
          </div>
        </div>

        <transition appear name="fade">
          <b-form v-on:submit.prevent="" class="needs-validation">

            <!-- Form card -->
            <div class="form-card mb-3">
              <div class="form-section-title">Shipping information</div>
              <div class="form-stack">

                <div class="field" :class="{ 'has-error': errors.firstName }">
                  <label for="m-firstName">First name</label>
                  <input id="m-firstName" v-model="currentUserFirstname"
                    class="form-input" :class="fieldClass('firstName')"
                    type="text" placeholder="John"
                    @blur="touchField('firstName')" @input="touchField('firstName')" />
                  <div class="field-error" v-if="errors.firstName">
                    <font-awesome-icon icon="exclamation-circle" /> First name is required
                  </div>
                </div>

                <div class="field" :class="{ 'has-error': errors.lastName }">
                  <label for="m-lastName">Last name</label>
                  <input id="m-lastName" v-model="currentUserLastname"
                    class="form-input" :class="fieldClass('lastName')"
                    type="text" placeholder="Doe"
                    @blur="touchField('lastName')" @input="touchField('lastName')" />
                  <div class="field-error" v-if="errors.lastName">
                    <font-awesome-icon icon="exclamation-circle" /> Last name is required
                  </div>
                </div>

                <div class="field" :class="{ 'has-error': errors.address1 }">
                  <label for="m-homeAddress1">Address line 1</label>
                  <input id="m-homeAddress1" v-model="currentUserAddress1"
                    class="form-input" :class="fieldClass('address1')"
                    type="text" placeholder="1234 Main St"
                    @blur="touchField('address1')" @input="touchField('address1')" />
                  <div class="field-error" v-if="errors.address1">
                    <font-awesome-icon icon="exclamation-circle" /> Address is required
                  </div>
                </div>

                <div class="field">
                  <label for="m-homeAddress2">Address line 2 <span class="opt">(Optional)</span></label>
                  <input id="m-homeAddress2" v-model="currentUserAddress2"
                    class="form-input" type="text" placeholder="Apartment or suite" />
                </div>

                <div class="field" :class="{ 'has-error': errors.state }">
                  <label for="m-homeState">State</label>
                  <select id="m-homeState" v-model="currentUserState"
                    class="form-input" :class="fieldClass('state')"
                    @change="onStateChange(); touchField('state')">
                    <option disabled value="">Select state</option>
                    <option v-for="state in stateList" :key="state" :value="state">
                      {{ state }} ({{ stateMap[state] || '' }})
                    </option>
                  </select>
                  <div class="field-error" v-if="errors.state">
                    <font-awesome-icon icon="exclamation-circle" /> Please select a state
                  </div>
                </div>

                <div class="field" :class="{ 'has-error': errors.city }">
                  <label for="m-homeCity">City</label>
                  <template v-if="filteredCities.length > 0">
                    <input id="m-homeCity" v-model="currentUserCity"
                      class="form-input" :class="fieldClass('city')"
                      type="text" list="m-cities-list"
                      autocomplete="off" placeholder="Start typing city name"
                      @blur="touchField('city')" @input="touchField('city')" />
                    <datalist id="m-cities-list">
                      <option v-for="city in filteredCities" :key="city" :value="city" />
                    </datalist>
                  </template>
                  <template v-else>
                    <input id="m-homeCity" v-model="currentUserCity"
                      class="form-input" :class="fieldClass('city')"
                      type="text" placeholder="Enter your city"
                      @blur="touchField('city')" @input="touchField('city')" />
                  </template>
                  <div class="field-error" v-if="errors.city">
                    <font-awesome-icon icon="exclamation-circle" /> City is required
                  </div>
                </div>

                <div class="field" :class="{ 'has-error': errors.zip }">
                  <label for="m-homeZip">Zip code</label>
                  <input id="m-homeZip" v-model="currentUserZip"
                    class="form-input" :class="fieldClass('zip')"
                    type="text" placeholder="90210"
                    @blur="touchField('zip')" @input="touchField('zip')" />
                  <div class="field-error" v-if="errors.zip">
                    <font-awesome-icon icon="exclamation-circle" /> Valid zip code required
                  </div>
                </div>

                <div class="field">
                  <label for="m-daytimePhone">Daytime phone</label>
                  <input id="m-daytimePhone" v-model="currentUserDayPhone"
                    class="form-input" type="text" placeholder="(555) 000-0000" />
                </div>

                <div class="field">
                  <label for="m-mobilePhone">Evening phone <span class="opt">(Optional)</span></label>
                  <input id="m-mobilePhone" v-model="currentUserEveningPhone"
                    class="form-input" type="text" placeholder="(555) 000-0000" />
                </div>

                <div class="field-divider"></div>

                <div class="field" :class="{ 'has-error': errors.email }">
                  <label for="m-personalEmail">Email address</label>
                  <input id="m-personalEmail" v-model="currentUserEmail"
                    v-on:input="setOrderEmailAddress(); touchField('email')"
                    class="form-input" :class="fieldClass('email')"
                    type="email" placeholder="you@example.com"
                    @blur="touchField('email')" />
                  <div class="field-error" v-if="errors.email">
                    <font-awesome-icon icon="exclamation-circle" /> Please enter a valid email address
                  </div>
                </div>

              </div>
            </div>

            <!-- Mini summary -->
            <div class="mini-summary mb-3">
              <div class="ms-row">
                <span class="ms-label">Products</span>
                <span class="ms-val">{{ pointsProduct | numToLocale }} pts</span>
              </div>
              <div class="ms-row">
                <span class="ms-label">Sales tax</span>
                <span class="ms-val blue">{{ bestBuySalesTax | numToLocale }} pts</span>
              </div>
              <div v-if="payTaxes" class="ms-row">
                <span class="ms-label">Income taxes</span>
                <span class="ms-val purple">{{ pointsTaxes | numToLocale }} pts</span>
              </div>
              <div class="ms-total">
                <span>Total</span>
                <span class="ms-total-val">{{ (totalPoints + bestBuySalesTax) | numToLocale }} pts</span>
              </div>
              <div class="ms-row">
                <span class="ms-label">Remaining balance</span>
                <span class="ms-val green">{{ remainingPoints | numToLocale }} pts</span>
              </div>
            </div>

            <!-- Sticky bottom bar -->
            <div class="mobile-sticky-bar">
              <!-- Acknowledgment checkbox -->
              <div class="ack-row" :class="{ 'ack-error': ackTouched && !acknowledged }">
                <input
                  id="ack-checkbox"
                  type="checkbox"
                  v-model="acknowledged"
                  class="ack-input"
                  @change="onAckChange"
                />
                <label for="ack-checkbox" class="ack-label">
                  I acknowledge and agree to the order information
                  <a class="ack-link" @click.prevent="showOrderInfoModal = true">outlined here.</a>
                </label>
              </div>
              <div class="field-error ack-field-error" v-if="ackTouched && !acknowledged">
                <font-awesome-icon icon="exclamation-circle" />
                Please acknowledge the order information to continue
              </div>
              <button
                :disabled="isDisabled || isPlacingOrder"
                class="btn-place-order"
                v-on:click="handlePlaceOrder"
              >
                <span v-if="!isPlacingOrder">Place order</span>
                <span v-else class="btn-loading">
                  <span class="btn-spinner"></span>
                  Placing order...
                </span>
              </button>
              <router-link class="btn-back-to-cart" :to="{ name: 'Cart' }">
                ← Back to cart
              </router-link>
            </div>
            
          </b-form>
        </transition>
      </div>
      <appFooter></appFooter>
      <navigationFooter></navigationFooter>
    </div>

    <!-- ═══════════════════════ DESKTOP ═══════════════════════ -->
    <div v-else class="mt-3">
      <appHeader></appHeader>
      <div class="container-fluid account p-4">

        <!-- Stepper -->
        <div class="cart-stepper mb-4">
          <div class="cs-step done">
            <div class="cs-dot"><font-awesome-icon icon="check" /></div>
            <span>View items</span>
          </div>
          <div class="cs-line"></div>
          <div class="cs-step active">
            <div class="cs-dot">2</div>
            <span>Shipping information</span>
          </div>
          <div class="cs-line"></div>
          <div class="cs-step">
            <div class="cs-dot">3</div>
            <span>Order summary</span>
          </div>
        </div>

        <transition appear name="fade">
          <b-form v-on:submit.prevent="" class="needs-validation">
            <div class="row d-flex justify-content-center">
              <div class="col-md-7 mb-4 pr-4">
                <div class="form-card">
                  <div class="form-section-title">Complete your information</div>
                  <p class="form-subtitle">Please enter the following information so we may process your order.</p>

                  <div class="form-section-label">Shipment address</div>

                  <div class="form-row-grid cols-2">
                    <div class="field" :class="{ 'has-error': errors.firstName }">
                      <label for="firstName">First name</label>
                      <input id="firstName" v-model="currentUserFirstname"
                        class="form-input" :class="fieldClass('firstName')"
                        type="text" placeholder="John"
                        @blur="touchField('firstName')" @input="touchField('firstName')" />
                      <div class="field-error" v-if="errors.firstName">
                        <font-awesome-icon icon="exclamation-circle" /> First name is required
                      </div>
                    </div>
                    <div class="field" :class="{ 'has-error': errors.lastName }">
                      <label for="lastName">Last name</label>
                      <input id="lastName" v-model="currentUserLastname"
                        class="form-input" :class="fieldClass('lastName')"
                        type="text" placeholder="Doe"
                        @blur="touchField('lastName')" @input="touchField('lastName')" />
                      <div class="field-error" v-if="errors.lastName">
                        <font-awesome-icon icon="exclamation-circle" /> Last name is required
                      </div>
                    </div>
                  </div>

                  <div class="form-row-grid cols-2">
                    <div class="field" :class="{ 'has-error': errors.address1 }">
                      <label for="homeAddress1">Address line 1</label>
                      <input id="homeAddress1" v-model="currentUserAddress1"
                        class="form-input" :class="fieldClass('address1')"
                        type="text" placeholder="1234 Main St"
                        @blur="touchField('address1')" @input="touchField('address1')" />
                      <div class="field-error" v-if="errors.address1">
                        <font-awesome-icon icon="exclamation-circle" /> Address is required
                      </div>
                    </div>
                    <div class="field">
                      <label for="homeAddress2">Address line 2 <span class="opt">(Optional)</span></label>
                      <input id="homeAddress2" v-model="currentUserAddress2"
                        class="form-input" type="text" placeholder="Apartment or suite" />
                    </div>
                  </div>

                  <div class="form-row-grid cols-3">
                    <div class="field" :class="{ 'has-error': errors.state }">
                      <label for="homeState">State</label>
                      <select id="homeState" v-model="currentUserState"
                        class="form-input" :class="fieldClass('state')"
                        @change="onStateChange(); touchField('state')">
                        <option disabled value="">Select state</option>
                        <option v-for="state in stateList" :key="state" :value="state">
                          {{ state }} ({{ stateMap[state] || '' }})
                        </option>
                      </select>
                      <div class="field-error" v-if="errors.state">
                        <font-awesome-icon icon="exclamation-circle" /> Please select a state
                      </div>
                    </div>
                    <div class="field" :class="{ 'has-error': errors.city }">
                      <label for="homeCity">City</label>
                      <template v-if="filteredCities.length > 0">
                        <input id="homeCity" v-model="currentUserCity"
                          class="form-input" :class="fieldClass('city')"
                          type="text" list="cities-list"
                          autocomplete="off" placeholder="Start typing city name"
                          @blur="touchField('city')" @input="touchField('city')" />
                        <datalist id="cities-list">
                          <option v-for="city in filteredCities" :key="city" :value="city" />
                        </datalist>
                      </template>
                      <template v-else>
                        <input id="homeCity" v-model="currentUserCity"
                          class="form-input" :class="fieldClass('city')"
                          type="text" placeholder="Enter your city"
                          @blur="touchField('city')" @input="touchField('city')" />
                      </template>
                      <div class="field-error" v-if="errors.city">
                        <font-awesome-icon icon="exclamation-circle" /> City is required
                      </div>
                    </div>
                    <div class="field" :class="{ 'has-error': errors.zip }">
                      <label for="homeZip">Zip code</label>
                      <input id="homeZip" v-model="currentUserZip"
                        class="form-input" :class="fieldClass('zip')"
                        type="text" placeholder="90210"
                        @blur="touchField('zip')" @input="touchField('zip')" />
                      <div class="field-error" v-if="errors.zip">
                        <font-awesome-icon icon="exclamation-circle" /> Valid zip code required
                      </div>
                    </div>
                  </div>

                  <div class="form-row-grid cols-2">
                    <div class="field">
                      <label for="daytimePhone">Daytime phone</label>
                      <input id="daytimePhone" v-model="currentUserDayPhone"
                        class="form-input" type="text" placeholder="(555) 000-0000" />
                    </div>
                    <div class="field">
                      <label for="mobilePhone">Evening phone <span class="opt">(Optional)</span></label>
                      <input id="mobilePhone" v-model="currentUserEveningPhone"
                        class="form-input" type="text" placeholder="(555) 000-0000" />
                    </div>
                  </div>

                  <div class="field-divider"></div>

                  <div class="form-row-grid cols-1">
                    <div class="field" :class="{ 'has-error': errors.email }">
                      <label for="personalEmail">Email address</label>
                      <input id="personalEmail" v-model="currentUserEmail"
                        v-on:input="setOrderEmailAddress(); touchField('email')"
                        class="form-input" :class="fieldClass('email')"
                        type="email" placeholder="you@example.com"
                        @blur="touchField('email')" />
                      <div class="field-error" v-if="errors.email">
                        <font-awesome-icon icon="exclamation-circle" /> Please enter a valid email address
                      </div>
                    </div>
                  </div>
                </div>

              </div>

              <!-- Summary sidebar -->
              <div class="col-md-3">
                <div class="summary-card">
                  <div class="sc-header">
                    <span class="sc-title">Order summary</span>
                  </div>
                  <div class="sc-rows">
                    <div class="sc-row">
                      <span class="sc-label">Products</span>
                      <span class="sc-val">{{ pointsProduct | numToLocale }} pts</span>
                    </div>
                    <div class="sc-divider"></div>
                    <div class="sc-row">
                      <span class="sc-label">Sales tax</span>
                      <span class="sc-val blue">{{ bestBuySalesTax | numToLocale }} pts</span>
                    </div>
                    <div v-if="payTaxes">
                      <div class="sc-divider"></div>
                      <div class="sc-row">
                        <span class="sc-label">Income taxes</span>
                        <span class="sc-val purple">{{ pointsTaxes | numToLocale }} pts</span>
                      </div>
                    </div>
                  </div>
                  <div class="sc-total-row">
                    <span class="sc-total-label">Total</span>
                    <span class="sc-total-val">{{ (totalPoints + bestBuySalesTax) | numToLocale }} pts</span>
                  </div>
                  <div class="sc-row" style="padding: .6rem 1rem">
                    <span class="sc-label">Remaining balance</span>
                    <span class="sc-val green">{{ remainingPoints | numToLocale }} pts</span>
                  </div>
                  <!-- Acknowledgment checkbox -->
                  <div class="ack-row" :class="{ 'ack-error': ackTouched && !acknowledged }">
                    <input
                      id="ack-checkbox"
                      type="checkbox"
                      v-model="acknowledged"
                      class="ack-input"
                      @change="onAckChange"
                    />
                    <label for="ack-checkbox" class="ack-label">
                      I acknowledge and agree to the order information
                      <a class="ack-link" @click.prevent="showOrderInfoModal = true">outlined here.</a>
                    </label>
                  </div>
                  <div class="field-error ack-field-error" v-if="ackTouched && !acknowledged">
                    <font-awesome-icon icon="exclamation-circle" />
                    Please acknowledge the order information to continue
                  </div>
                  <div class="sc-action">
                    <button
                      :disabled="isDisabled || isPlacingOrder"
                      class="btn-place-order"
                      v-on:click="handlePlaceOrder"
                    >
                      <span v-if="!isPlacingOrder">Place order</span>
                      <span v-else class="btn-loading">
                        <span class="btn-spinner"></span>
                        Placing order...
                      </span>
                    </button>
                  </div>
                  <router-link class="btn-back-to-cart" :to="{ name: 'Cart' }">
                    ← Back to cart
                  </router-link>
                  <div class="sc-note">
                    Points are subject to change based on availability and vendor fulfillment.
                  </div>
                </div>
              </div>

            </div>
          </b-form>
        </transition>
      </div>
      <appFooter></appFooter>
    </div>

    <!-- Order info modal -->
    <b-modal
      v-model="showOrderInfoModal"
      title="Important order information"
      ok-only
      ok-title="Got it"
      ok-variant="primary"
      centered
      size="md"
      header-class="order-modal-header"
      body-class="order-modal-body"
    >
      <ul class="order-modal-list">
        <li>
          <strong>All sales are final</strong> — orders cannot be changed, canceled, refunded, or returned once submitted.
        </li>
        <li>
          Gift cards are <strong>digital</strong>, delivered electronically, and must be activated within <strong>120 days</strong> of issuance.
        </li>
        <li>
          Claim requests must be submitted within <strong>30 days</strong> of order.
        </li>
        <li>
          The cost of any prize must, by law, be <strong>reported as additional income</strong> and will appear on your pay-stub based on the approximate market value.
        </li>
      </ul>
    </b-modal>
  </div>
</template>
<script>
import Header from "@/components/Header.vue";
import Footer from "@/components/Footer.vue";
import { mapState } from "vuex";
import { trim } from "core-js/internals/string-trim";
import { isMobile, isTablet } from "mobile-device-detect";
import navigationFooter from "../../components/navigationFooter.vue";
import FrontendService from "../../services/FrontendService"
import stateCities from '@/assets/state_cities.json';

export default {
  components: {
    appHeader: Header,
    appFooter: Footer,
    navigationFooter: navigationFooter,
  },
  data() {
    return {
      checkoutProgressMax: 100,
      checkoutProgressCurrent: 60,
      newUserEmail: null,
      newUserFirstname: null,
      newUserLastname: null,
      newUserAddress1: null,
      newUserAddress2: null,
      newUserCity: null,
      newUserState: null,
      newUserZip: null,
      newUserDayPhone: null,
      newUserEveningPhone: null,
      bestBuySalesTax: 0,
      isDisabled: false,
      isPlacingOrder: false,
      touched: {},
      stateCities: stateCities,
      stateMap: {
        "Alabama": "AL", "Alaska": "AK", "Arizona": "AZ", "Arkansas": "AR", "California": "CA",
        "Colorado": "CO", "Connecticut": "CT", "Delaware": "DE", "Florida": "FL", "Georgia": "GA",
        "Hawaii": "HI", "Idaho": "ID", "Illinois": "IL", "Indiana": "IN", "Iowa": "IA",
        "Kansas": "KS", "Kentucky": "KY", "Louisiana": "LA", "Maine": "ME", "Maryland": "MD",
        "Massachusetts": "MA", "Michigan": "MI", "Minnesota": "MN", "Mississippi": "MS",
        "Missouri": "MO", "Montana": "MT", "Nebraska": "NE", "Nevada": "NV", "New Hampshire": "NH",
        "New Jersey": "NJ", "New Mexico": "NM", "New York": "NY", "North Carolina": "NC",
        "North Dakota": "ND", "Ohio": "OH", "Oklahoma": "OK", "Oregon": "OR", "Pennsylvania": "PA",
        "Rhode Island": "RI", "South Carolina": "SC", "South Dakota": "SD", "Tennessee": "TN",
        "Texas": "TX", "Utah": "UT", "Vermont": "VT", "Virginia": "VA", "Washington": "WA",
        "West Virginia": "WV", "Wisconsin": "WI", "Wyoming": "WY",
        "District of Columbia": "DC", "Puerto Rico": "PR"
      },
      acknowledged: false,
      ackTouched: false,
      showOrderInfoModal: false,
    };
  },

  computed: {
    ...mapState({
      userId: (state) => state.user.profile.id,
      pointsUser: (state) => state.user.profile.pointsAvailable,
      pointsUnapproved: (state) => state.cart.unapproved.productPoints,
      username: (state) => state.user.profile.username,
      userEmail: (state) => state.user.profile.email,
      userFirstname: (state) => state.user.profile.firstName,
      userLastname: (state) => state.user.profile.lastName,
      userAddress1: (state) => state.user.profile.address1,
      userAddress2: (state) => state.user.profile.address2,
      userCity: (state) => state.user.profile.city,
      userState: (state) => state.user.profile.state,
      userZip: (state) => state.user.profile.zip,
      userDayPhone: (state) => state.user.profile.dayPhone,
      userEveningPhone: (state) => state.user.profile.eveningPhone,
      pointsProduct: (state) => state.cart.summary.productPoints,
      pointsTaxes: (state) => state.cart.summary.taxPoints,
      payTaxes: (state) => state.cart.usePointsToPayTaxes,
      cartItems: (state) => state.cart.items,
    }),

    isMobileOrTablet() {
      return isMobile || isTablet;
    },
    totalPoints() {
      let total = this.pointsProduct;
      if (this.payTaxes) total += this.pointsTaxes;
      return total;
    },
    pointsAvailable() {
      return this.pointsUser - this.pointsUnapproved;
    },
    remainingPoints() {
      return this.pointsAvailable - this.totalPoints - this.bestBuySalesTax;
    },

    // ── computed field errors (only active after field is touched) ──
    errors() {
      return {
        firstName: this.touched.firstName && !this.currentUserFirstname?.trim(),
        lastName:  this.touched.lastName  && !this.currentUserLastname?.trim(),
        address1:  this.touched.address1  && !this.currentUserAddress1?.trim(),
        state:     this.touched.state     && !this.currentUserState?.trim(),
        city:      this.touched.city      && !this.currentUserCity?.trim(),
        zip:       this.touched.zip && !/^\d{5}(-\d{4})?$/.test((this.currentUserZip || '').trim()),
        email:     this.touched.email     && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.currentUserEmail || ''),
      };
    },

    stateList() {
      return Object.keys(this.stateCities).sort();
    },
    filteredCities() {
      if (this.currentUserState && this.stateCities[this.currentUserState]) {
        return this.stateCities[this.currentUserState];
      }
      return [];
    },

    // ── two-way computed user fields ──
    currentUserEmail: {
      get() { return this.newUserEmail === null ? this.userEmail : this.newUserEmail; },
      set(val) { this.newUserEmail = val; },
    },
    currentUserFirstname: {
      get() { return this.newUserFirstname === null ? this.userFirstname : this.newUserFirstname; },
      set(val) { this.newUserFirstname = val; },
    },
    currentUserLastname: {
      get() { return this.newUserLastname === null ? this.userLastname : this.newUserLastname; },
      set(val) { this.newUserLastname = val; },
    },
    currentUserAddress1: {
      get() { return this.newUserAddress1 === null ? this.userAddress1 : this.newUserAddress1; },
      set(val) { this.newUserAddress1 = val; },
    },
    currentUserAddress2: {
      get() { return this.newUserAddress2 === null ? this.userAddress2 : this.newUserAddress2; },
      set(val) { this.newUserAddress2 = val; },
    },
    currentUserCity: {
      get() { return this.newUserCity === null ? this.userCity : this.newUserCity; },
      set(val) { this.newUserCity = val; },
    },
    currentUserState: {
      get() { return this.newUserState === null ? this.userState : this.newUserState; },
      set(val) { this.newUserState = val; },
    },
    currentUserZip: {
      get() { return this.newUserZip === null ? this.userZip : this.newUserZip; },
      set(val) { this.newUserZip = val; },
    },
    currentUserDayPhone: {
      get() { return this.newUserDayPhone === null ? this.userDayPhone : this.newUserDayPhone; },
      set(val) { this.newUserDayPhone = val; },
    },
    currentUserEveningPhone: {
      get() { return this.newUserEveningPhone === null ? this.userEveningPhone : this.newUserEveningPhone; },
      set(val) { this.newUserEveningPhone = val; },
    },
  },

  beforeMount() {
    this.scrollToTop();
  },

  methods: {
    scrollToTop() {
      window.scrollTo(0, 0);
    },

    onStateChange() {
      this.currentUserCity = '';
    },

    touchField(field) {
      this.$set(this.touched, field, true);
    },

    fieldClass(field) {
      if (!this.touched[field]) return '';
      return this.errors[field] ? 'is-invalid' : 'is-valid';
    },

    // ── called by Place order button ──
    handlePlaceOrder() {
        this.ackTouched = true; // ← trigger ack error if unchecked

      // Check acknowledgment first
      if (!this.acknowledged) {
        return;
      }
      // 1. Mark all mandatory fields as touched so errors show
      ['firstName', 'lastName', 'address1', 'state', 'city', 'zip', 'email']
        .forEach(f => this.$set(this.touched, f, true));

      // 2. Wait for Vue to recompute errors with updated touched state
      this.$nextTick(() => {
        const hasErrors = Object.values(this.errors).some(Boolean);

        if (hasErrors) {
          // Scroll to first invalid field
          const firstError = document.querySelector('.form-input.is-invalid');
          if (firstError) {
            firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
            firstError.focus();
          }
          return; // stop — do NOT enable loader or call orderCart
        }

        // 3. All valid — show loader and proceed
        this.isPlacingOrder = true;
        this.updateInformation();
      });
    },

    onAckChange() {
      this.ackTouched = true;
      if (this.acknowledged) {
        // User just checked it — open modal to confirm they've read it
        this.showOrderInfoModal = true;
      }
    },
    setOrderEmailAddress() {
      this.$store.dispatch("cart/setOrderEmailAddress", this.currentUserEmail);
    },

    // ── always validates and saves, never skips shipping ──
    updateInformation() {
      if (!this.isUserDataValid()) {
        this.isPlacingOrder = false;
        return;
      }
      this.$store
        .dispatch("user/setProfile", {
          id: this.userId,
          firstName: this.currentUserFirstname,
          lastName:  this.currentUserLastname,
          address1:  this.currentUserAddress1,
          address2:  this.currentUserAddress2,
          city:      this.currentUserCity,
          zip:       this.currentUserZip,
          state:     this.currentUserState,
          dayPhone:  this.currentUserDayPhone,
          eveningPhone: this.currentUserEveningPhone,
        })
        .then(() => {
          this.$router.push({ name: "Order Summary" });
        })
        .catch(() => {
          this.isPlacingOrder = false; // reset on API failure
          this.showError("Failed to update profile");
        });
    },

    isUserDataValid() {
      return (
        this.currentUserFirstname != null && trim(this.currentUserFirstname).length > 0 &&
        this.currentUserLastname  != null && trim(this.currentUserLastname).length  > 0 &&
        this.currentUserAddress1  != null && trim(this.currentUserAddress1).length  > 0 &&
        this.currentUserCity      != null && trim(this.currentUserCity).length      > 0 &&
        this.currentUserZip       != null && trim(this.currentUserZip).length       > 0 &&
        this.currentUserState     != null && trim(this.currentUserState).length     > 0 &&
        this.currentUserDayPhone  != null && trim(this.currentUserDayPhone).length  > 0 &&
        this.currentUserEmail     != null && trim(this.currentUserEmail).length     > 0
      );
    },

    getCartDetails() {
      this.isDisabled = true;
      FrontendService.getCart({ offset: 0, limit: 20, zipcode: this.currentUserZip })
        .then((response) => {
          this.bestBuySalesTax = response.data.total_sales_tax;
          this.isDisabled = false;
        })
        .catch((e) => {
          this.isDisabled = false;
          console.log(e.response);
        });
    },

    calculateBestBuySalesTax() {
      let bestBuyProducts = [];
      this.cartItems.forEach((items) => {
        if (items.supplierName == 'Best Buy' && items.sku != '') {
          bestBuyProducts.push({ sku: items.sku, qty: items.quantity });
        }
      });
      if (this.currentUserZip && bestBuyProducts.length > 0) {
        this.isDisabled = true;
        FrontendService.fetchBestBuySalesTax({ skuid: bestBuyProducts, zipcode: this.currentUserZip })
          .then((response) => {
            this.isDisabled = false;
            this.bestBuySalesTax = response.data.totalTaxPoints;
          })
          .catch(() => { this.isDisabled = false; });
      }
    },

    showSuccess(message, onClose) {
      this.$bvModal.msgBoxOk(message, {
        size: "sm", headerClass: "p-2 border-bottom-0",
        footerClass: "p-2 border-top-0", centered: true,
      }).then(() => { onClose(); });
    },

    showError(message) {
      this.$bvModal.msgBoxOk(message, {
        size: "sm", headerClass: "p-2 border-bottom-0",
        footerClass: "p-2 border-top-0", okVariant: "danger", centered: true,
      });
    },
  },

  created() {
    this.$store.dispatch("cart/fetchCart");
    this.$store.dispatch("cart/fetchCartSummary");
    this.$store.dispatch("user/fetchProfile");
    this.getCartDetails();
  },

  watch: {
    currentUserZip() {
      this.getCartDetails();
    },
  },
};
</script>
<style lang="scss">
.order {
  img {
    max-width: 100%;
  }

  .prod-data {
    color: $gray-600;
    font-size: 0.8rem;

    strong {
      display: block;
    }
  }
}

.modal-body {
  padding: 2rem 2rem 0 2rem !important;
}

/* ── Stepper (same as Cart.vue) ── */
.cart-stepper { display: flex; align-items: center; }
.cs-step { display: flex; align-items: center; gap: 7px; font-size: 13px; color: $gray-500; }
.cs-step.active { color: $primary; font-weight: 500; }
.cs-step.done   { color: $success; }
.cs-dot {
  width: 22px; height: 22px; border-radius: 50%;
  border: 2px solid $gray-300;
  display: flex; align-items: center; justify-content: center;
  font-size: 11px; font-weight: 500; background: #fff; color: $gray-500; flex-shrink: 0;
}
.cs-step.active .cs-dot { border-color: $primary; color: $primary; background: lighten($primary, 44%); }
.cs-step.done   .cs-dot { border-color: $success; color: #fff; background: $success; font-size: 9px; }
.cs-line { flex: 1; height: 1px; background: $gray-200; min-width: 20px; }

/* ── Notice banner ── */
.cart-notice-banner {
  background: $gray-100; border: 1px solid $gray-200;
  border-radius: $border-radius; padding: .75rem 1rem;
  font-size: 12.5px; color: $gray-700;
}
.cart-notice-summary { font-size: 13px; font-weight: 500; color: $gray-700; margin-bottom: 6px; }
.cart-notice-list { margin: 0; padding-left: 16px; li { padding: 2px 0; line-height: 1.55; } }

/* ── Form card ── */
.form-card {
  background: #fff; border: 1px solid $gray-200;
  border-radius: $border-radius; padding: 1.25rem;
}
.form-section-title { font-size: 1rem; font-weight: 500; margin-bottom: .25rem; }
.form-subtitle { font-size: 0.825rem; color: $gray-600; margin-bottom: 1rem; }
.form-section-label { font-size: 0.8rem; font-weight: 500; color: $gray-600; text-transform: uppercase; letter-spacing: .04em; margin-bottom: .75rem; }

/* ── Field grid ── */
.form-row-grid {
  display: grid; gap: .85rem; margin-bottom: .85rem;
  &.cols-1 { grid-template-columns: 1fr; }
  &.cols-2 { grid-template-columns: 1fr 1fr; }
  &.cols-3 { grid-template-columns: 2fr 3fr 2fr; }
}
.form-stack { display: flex; flex-direction: column; gap: .85rem; }

/* ── Individual field ── */
.field { display: flex; flex-direction: column; gap: 4px; }
.field label { font-size: 12.5px; font-weight: 500; color: $gray-700; margin: 0; }
.field label .opt { font-weight: 400; color: $gray-700; margin-left: 4px; }
.form-input {
  height: 36px; border: 1px solid $gray-300; border-radius: $border-radius-sm;
  padding: 0 10px; font-size: 13.5px; background: #fff; color: $gray-900;
  outline: none; transition: border-color .15s, box-shadow .15s; width: 100%;
  &:focus { border-color: $primary; box-shadow: 0 0 0 2px rgba($primary, .12); }
  &.is-invalid { border-color: $danger; background: lighten($danger, 47%); }
  &.is-valid   { border-color: $success; }
}
.field-error {
  display: flex; align-items: center; gap: 4px;
  font-size: 11.5px; color: $danger; margin-top: 1px;
}
.field-divider { height: 1px; background: $gray-100; margin: .25rem 0; }

/* ── Warning box ── */
.warn-box {
  background: lighten(#f59e0b, 40%);
  border: 1px solid lighten(#f59e0b, 20%);
  border-radius: $border-radius; padding: 1rem;
}
.warn-row { display: flex; align-items: flex-start; gap: 10px; margin-bottom: .75rem; &:last-child { margin-bottom: 0; } }
.warn-icon {
  width: 28px; height: 28px; border-radius: 50%; background: $gray-600;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0; margin-top: 2px;
}
.warn-text { font-size: 12.5px; color: $gray-700; line-height: 1.5; }

/* ── Summary card ── */
.summary-card {
  background: #fff; border: 1px solid $gray-200;
  border-radius: $border-radius; overflow: hidden; position: sticky; top: 80px;
}
.sc-header { padding: .85rem 1rem; border-bottom: 1px solid $gray-200; }
.sc-title   { font-size: 0.95rem; font-weight: 500; }
.sc-rows    { padding: .25rem 0; }
.sc-row     { display: flex; justify-content: space-between; align-items: center; padding: .55rem 1rem; font-size: 0.825rem; }
.sc-label   { color: $gray-700; }
.sc-val     { font-weight: 500; color: $gray-800; &.blue { color: $primary; } &.green { color: $success; } &.purple { color: #9b51e0; } }
.sc-divider { height: 1px; background: $gray-100; margin: 0 1rem; }
.sc-total-row { display: flex; justify-content: space-between; align-items: center; padding: .75rem 1rem; background: $gray-100; border-top: 1px solid $gray-200; border-bottom: 1px solid $gray-200; }
.sc-total-label { font-size: 0.9rem; font-weight: 500; }
.sc-total-val   { font-size: 1rem; font-weight: 700; color: $danger; }
.sc-action { padding: .85rem 1rem .25rem; }
.sc-note { padding: .65rem 1rem; font-size: 0.72rem; color: $gray-700; line-height: 1.5; border-top: 1px solid $gray-200; }

/* ── Place order button ── */
.btn-place-order {
  display: flex; align-items: center; justify-content: center; gap: 8px;
  width: 100%; background: $primary; border: none;
  border-radius: $border-radius-sm; padding: 11px;
  font-size: 0.9rem; font-weight: 500; color: #fff;
  cursor: pointer; transition: background .15s ease;
  &:hover:not(:disabled) { background: darken($primary, 8%); }
  &:disabled { background: lighten($primary, 25%); cursor: not-allowed; }
}
.btn-spinner {
  display: inline-block; width: 14px; height: 14px;
  border: 2px solid rgba(#fff, .3); border-top-color: #fff;
  border-radius: 50%; animation: spin .65s linear infinite;
}
.btn-loading { display: flex; align-items: center; gap: 7px; }
@keyframes spin { to { transform: rotate(360deg); } }

/* ── Back to cart link ── */
.btn-back-to-cart {
  display: block; text-align: center; font-size: 13px; color: $primary;
  padding: .5rem; margin: .1rem 0; text-decoration: none;
  &:hover { text-decoration: underline; color: darken($primary, 10%); }
}

/* ── Mobile mini summary ── */
.mini-summary {
  background: #fff; border: 1px solid $gray-200;
  border-radius: $border-radius; overflow: hidden;
}
.ms-row { display: flex; justify-content: space-between; padding: .55rem 1rem; font-size: 13px; border-bottom: 1px solid $gray-100; }
.ms-label { color: $gray-700; }
.ms-val { font-weight: 500; color: $gray-800; &.blue { color: $primary; } &.green { color: $success; } &.purple { color: #9b51e0; } }
.ms-total { display: flex; justify-content: space-between; padding: .7rem 1rem; background: $gray-100; font-weight: 500; font-size: 13.5px; }
.ms-total-val { font-weight: 700; color: $danger; }

/* ── Mobile sticky bar ── */
.mobile-sticky-bar {
  background: #fff;
  border-top: 1px solid $gray-200;
  padding: .85rem 1rem;
  margin-top: 1rem;
  position: sticky;
  bottom: 0;
  z-index: 200;          // ← higher than navigationFooter
  padding-bottom: calc(.85rem + 60px);  // ← 60px = approx nav footer height
}
// On the mobile container div
.container-fluid.cart-mobile-wrap {
  padding-bottom: 140px;  // ← enough space for sticky bar + nav footer
}
/* ── Responsive ── */
@media (max-width: 576px) {
  .form-row-grid.cols-3 { grid-template-columns: 1fr 1fr; }
  .form-row-grid.cols-3 > :last-child { grid-column: span 2; }
}

/* ── Acknowledgment row ── */
.ack-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: #fff;
  border: 1px solid $gray-200;
  border-radius: $border-radius-sm;
  padding: .75rem 1rem;
  margin-bottom: .5rem;
  transition: border-color .15s;

  &.ack-error {
    border-color: $danger;
    background: lighten($danger, 47%);
  }
}
.ack-input {
  margin-top: 2px;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  cursor: pointer;
  accent-color: $primary;
}
.ack-label {
  font-size: 13px;
  color: $gray-800;
  line-height: 1.5;
  cursor: pointer;
  margin: 0;
}
.ack-link {
  color: $primary;
  text-decoration: underline;
  cursor: pointer;
  font-weight: 500;
  &:hover { color: darken($primary, 10%); }
}
.ack-field-error {
  margin-bottom: .5rem;
}

/* ── Order info modal ── */
.order-modal-body {
  padding: 1.25rem 1.5rem !important;
}
.order-modal-list {
  padding-left: 1.25rem;
  margin: 0;
  li {
    padding: .4rem 0;
    font-size: 14px;
    line-height: 1.6;
    color: $gray-700;
    border-bottom: 1px solid $gray-100;
    &:last-child { border-bottom: none; }
  }
}

.order-modal-header {
  padding-bottom: .25rem !important;
  border-bottom: none !important;        // removes the divider line adding space
}
.order-modal-body {
  padding: .25rem 1.5rem 1.25rem !important;  // ← reduce top padding
}

</style>
